import { UseFormReturn } from 'react-hook-form';
import { FormMode } from '@hooks/useFormEdit';
import { ReportStatus } from '@declarations/common/enums/ReportStatus';
import { IncidentType } from '@declarations/common/enums/IncidentType';
import { UserType } from '@declarations/common/enums/UserType';
import { Fields } from '@components/CRUDForm/declarations/FormField';
import { Incident, IncidentTypes } from '@declarations/models/reports/Incident';
import { yesNoOptions } from '../../../const';
import isHighRisk from '../../../../utils/isHighRisk';

type Params = {
  name?: string;
  form: UseFormReturn<Incident>;
  mode: FormMode;
  userType: UserType;
};

type RequiredFields = Exclude<IncidentTypes[IncidentType.Wound], undefined>;

function getWoundFields(params: Params): {
  fields: Fields<RequiredFields>;
  fileFields: Fields<RequiredFields>;
} {
  const calculatedStatus = params.form.getValues('calculatedStatus');
  const disabledField = calculatedStatus === ReportStatus.Completed && params.userType !== UserType.SuperAdmin;

  const getFieldName = (field: keyof RequiredFields) => {
    const prefix = params?.name ? `${params.name}.` : '';
    return `${prefix}${IncidentType.Wound}.${field}`;
  };

  const makeCondition = ({ form }: any) => {
    if (params && 'name' in params) {
      return form.watch('typeOfIncident') === params.name
        && form.watch(`${params.name}.causeOfUnplannedDeath`) === IncidentType.Wound;
    }

    return form.watch('typeOfIncident') === IncidentType.Wound;
  };

  return {
    fields: [
      [
        {
          type: 'radio',
          fullWidth: true,
          name: getFieldName('wasWoundDevelopedInFacility'),
          label: 'Was the wound developed in the facility?',
          options: yesNoOptions,
          condition: makeCondition,
          props: {
            inputLabel: false,
            sx: { maxWidth: 292 },
            disabled: disabledField,
          },
        },
      ],
      [
        {
          type: 'radio',
          fullWidth: true,
          name: getFieldName('wasThereTreatmentPlanInPlaceThatWasFollowed'),
          label: 'Was there a treatment plan in place that was followed?',
          options: yesNoOptions,
          condition: makeCondition,
          props: {
            inputLabel: false,
            sx: { maxWidth: 292 },
            disabled: disabledField,
          },
        },
      ],
      [
        {
          type: 'radio',
          fullWidth: true,
          name: getFieldName('doesTheResidentHaveConditionThatProhibitsSkinHealing'),
          label: 'Does the resident have a condition or diagnosis that prohibits skin healing?',
          options: yesNoOptions,
          condition: makeCondition,
          props: {
            inputLabel: false,
            sx: { maxWidth: 292 },
            disabled: disabledField,
          },
        },
      ],
      [
        {
          type: 'radio',
          fullWidth: true,
          name: getFieldName('doesResidentHaveHistory'),
          label: 'Does the resident have a history of non-compliance with treatments/bathing and/or diet?',
          options: yesNoOptions,
          condition: makeCondition,
          props: {
            inputLabel: false,
            sx: { maxWidth: 292 },
            disabled: disabledField,
          },
        },
      ],
      [
        {
          type: 'radio',
          fullWidth: true,
          name: getFieldName('didResidentRequireHospitalization'),
          label: 'Did the resident require hospitalization?',
          options: yesNoOptions,
          condition: makeCondition,
          props: {
            inputLabel: false,
            sx: { maxWidth: 292 },
            disabled: disabledField,
          },
        },
      ],
      [
        {
          type: 'radio',
          fullWidth: true,
          name: getFieldName('didEventOrIncidentResultInPermanentInjuryOrDeath'),
          label: 'Did the event/incident result in permanent injury or death?',
          options: yesNoOptions,
          condition: makeCondition,
          props: {
            inputLabel: false,
            sx: { maxWidth: 292 },
            disabled: disabledField,
          },
        },
      ],
    ],
    fileFields: [
      [
        {
          type: 'file',
          required: false,
          name: getFieldName('treatmentRecordFiles'),
          label: 'Treatment Record',
          condition: makeCondition,
          hidden: params.mode === FormMode.Create || !isHighRisk(params.form.watch('riskPoints')),
        },
      ],
      [
        {
          type: 'file',
          required: false,
          name: getFieldName('woundAssesmentsFiles'),
          label: 'Wound Assessments',
          condition: makeCondition,
          hidden: params.mode === FormMode.Create || !isHighRisk(params.form.watch('riskPoints')),
        },
      ],
    ],
  };
}

export default getWoundFields;
